/*
 *
 * Staff
 *
 */

import React from "react"
import { connect } from "react-redux"
import { Helmet } from "react-helmet"
import { Icon, Table } from "components"
import { Tooltip, Modal, notification, Select } from "antd"
import { setBreadcrumb, setLoader, setTitle } from "store/actions"
import { Props, State } from "./types"

import Strings from "utils/strings"
import { API, Endpoints } from "utils/api"
import StaffEditorModal, {
	ASSIGNABLE_ROLES,
	getRoleLabel,
} from "./StaffEditorModal"
import "./styles.scss"

export class Staff extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props)

		this.state = {
			staff: [],
			editorVisible: false,
			editorStaff: null,
			openConfirmResendInviteModal: false,
			roleFilter: "",
		}
	}

	componentDidMount() {
		const { dispatch } = this.props

		dispatch(setTitle(""))
		dispatch(setBreadcrumb(null))

		this.getStaff()
	}

	async getStaff() {
		const { dispatch } = this.props

		dispatch(setLoader(true))

		try {
			const response = await API.get({
				url: Endpoints.uriStaff(),
			})

			if (response.ok) {
				this.setState({
					staff: response.data.results.staff || [],
				})
			}
		} catch (err) {
			console.log("API Error", err)
		}

		dispatch(setLoader(false))
	}

	resendEmail = async (staff: any) => {
		const { dispatch } = this.props

		dispatch(setLoader(true))

		try {
			const response = await API.patch({
				url: Endpoints.uriStaff(`resend-invite/${staff._id}`),
			})

			if (response.ok && response.data.results) {
				this.setState({
					staff: response.data.results.staff,
					openConfirmResendInviteModal: false,
				})
				notification.success({
					message: Strings.emails.invite,
					description: response.data.message,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(false))
	}

	getStatus(status: any) {
		if (status === "confirmed") {
			return Strings.staff.confirmed
		}
		return Strings.staff.pending
	}

	archiveStaff = async (id: any) => {
		const { dispatch } = this.props

		dispatch(setLoader(true))

		try {
			const response = await API.delete({
				url: Endpoints.uriStaff(id),
			})

			if (response?.ok) {
				const { staff = [] } = response.data.results

				this.setState({ staff })

				notification.success({
					message: Strings.staff.archiveStaff,
					description: Strings.staff.archived,
					placement: "bottomRight",
					duration: 5,
				})
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(false))
	}

	toggleStaff = async (user: any) => {
		const { dispatch } = this.props

		dispatch(setLoader(true))

		try {
			const body = {
				isActive: !user.isActive,
			}

			const response = await API.patch({
				url: Endpoints.uriStaff(user._id),
				data: body,
			})

			if (response?.ok) {
				const { staff = [] } = response.data.results

				this.setState({ staff })

				notification.success({
					message: user.isActive
						? Strings.staff.disableStaff
						: Strings.staff.enableStaff,
					description: user.isActive
						? Strings.staff.disabled
						: Strings.staff.enabled,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}
		dispatch(setLoader(false))
	}

	renderConfirmResendInvite = () => {
		const { openConfirmResendInviteModal } = this.state

		return (
			<Modal
				className="resendInvite"
				style={{ textAlign: "center" }}
				visible={openConfirmResendInviteModal}
				cancelText={Strings.generic.close}
				okText={Strings.authentication.resendInviteEmail}
				onOk={() => this.resendEmail(openConfirmResendInviteModal)}
				onCancel={() => this.setState({ openConfirmResendInviteModal: false })}
				title={null}
				closable={false}
				bodyStyle={{ minHeight: 200 }}
			>
				<Icon name="paper-plane" style={{ fontSize: "50px" }} />
				<div className="title">
					{openConfirmResendInviteModal
						? Strings.formatString(
								Strings.authentication.confirmResendInviteEmail,
								openConfirmResendInviteModal.name
							)
						: Strings.authentication.resendInviteEmail}
				</div>
			</Modal>
		)
	}

	renderStatus = (elem: any) => {
		if (elem.value === "confirmed")
			return (
				<div className="TableTag --tag-success">
					<span>{this.getStatus("confirmed")}</span>
				</div>
			)

		return (
			<Tooltip title={Strings.authentication.resendInviteEmail}>
				<div
					className="TableTag --tag-warning --tag-clickable"
					onClick={() =>
						this.setState({
							openConfirmResendInviteModal: elem.cell.row.original,
						})
					}
				>
					<Icon name="refresh" className="resend" />
					<span>{this.getStatus("pending")}</span>
				</div>
			</Tooltip>
		)
	}

	render() {
		const { staff, roleFilter, editorVisible, editorStaff } = this.state
		const { user } = this.props

		const filteredStaff = roleFilter
			? staff.filter((s: any) => s.role === roleFilter)
			: staff

		const roleFilterComponent = (
			<Select
				value={roleFilter || undefined}
				placeholder={Strings.placeholders.role}
				allowClear
				style={{ minWidth: 180 }}
				onChange={(value: any) => this.setState({ roleFilter: value || "" })}
			>
				{ASSIGNABLE_ROLES.map((role) => (
					<Select.Option key={`filter_role_${role}`} value={role}>
						{getRoleLabel(role)}
					</Select.Option>
				))}
			</Select>
		)

		return (
			<React.Fragment>
				<Helmet>
					<title>{Strings.sidebar.staff}</title>
					<meta name="description" content="Description of Staff" />
				</Helmet>
				<Table
					title={{
						icon: "user2",
						title: Strings.sidebar.staff,
					}}
					headerOptions={[
						{
							type: "custom",
							icon: "filter",
							customComponent: roleFilterComponent,
							marginRightAuto: true,
						},
					]}
					filtrationApi={{
						showGlobalSearch: true,
						showColumnSearch: false,
					}}
					data={filteredStaff}
					columns={[
						{
							Header: Strings.fields.photo,
							id: "photo",
							accessor: (row: any) => row.photo,
							type: "image",
							maxWidth: 65,
						},
						{
							Header: Strings.fields.name,
							id: "name",
							accessor: (row: any) => row.name || "-",
						},
						{
							Header: Strings.fields.email,
							id: "email",
							accessor: (row: any) => row.email || "-",
						},
						{
							Header: Strings.fields.phone,
							id: "phone",
							accessor: (row: any) => row.phone || "-",
						},
						{
							Header: Strings.staff.status,
							id: "status",
							accessor: (row: any) =>
								row.isConfirmed ? "confirmed" : "pending",
							Cell: this.renderStatus,
						},
						{
							Header: Strings.profile.role,
							id: "role",
							accessor: (row: any) => getRoleLabel(row.role),
						},
					]}
					fullPage
					isSinglePage
					filterable
					sortable
					add={{
						onClick: () =>
							this.setState({ editorStaff: null, editorVisible: true }),
					}}
					actions={{
						edit: (original: any) => ({
							disabled:
								original.role === "sysadmin" && user?.role !== "sysadmin",
							onClick: () => {
								this.setState({
									editorStaff: JSON.parse(JSON.stringify(original)),
									editorVisible: true,
								})
							},
						}),
						remove: (original: any) => ({
							disabled: original.role === "sysadmin",
							onClick: () => this.archiveStaff(original._id),
						}),
						toggle: (original: any) => ({
							value: original.isActive,
							disabled: original.role === "sysadmin",
							onChange: () => this.toggleStaff(original),
						}),
					}}
					getRowStyle={(row: any) => {
						if (!row.original.isActive) {
							return {
								opacity: 0.5,
								backgroundColor: "#f5f5f5",
							}
						}
						return {}
					}}
				/>
				{this.renderConfirmResendInvite()}
				<StaffEditorModal
					visible={editorVisible}
					staff={editorStaff}
					onClose={() =>
						this.setState({ editorVisible: false, editorStaff: null })
					}
					onSuccess={() => {
						this.setState({ editorVisible: false, editorStaff: null })
						this.getStaff()
					}}
				/>
			</React.Fragment>
		)
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language,
	user: state.user,
})

export default connect(mapStateToProps)(Staff)
